import { useState, useEffect, useRef, useCallback } from 'react';

export const useTurnstile = (elementId: string, options: any = {}) => {
  const [token, setToken] = useState('');
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState(null);
  const widgetId = useRef(null);

  // Load Turnstile script
  useEffect(() => {
    if (!elementId) {
        (window as any).turnstile?.remove(widgetId.current);
        widgetId.current = null;
        return
    }

    if (!process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY) {
      setError('Turnstile Site Key is missing');
      return;
    }

    if ((window as any).turnstile) {
      setIsLoaded(true);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
    script.async = true;
    script.defer = true;
    script.onload = () => setIsLoaded(true);
    script.onerror = () => setError('Failed to load Turnstile');
    document.head.appendChild(script);

    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, [elementId]);

  // Success callback
  const handleSuccess = useCallback((turnstileToken) => {
    setToken(turnstileToken);
    setError(null);
    if (options.onSuccess) {
      options.onSuccess(turnstileToken);
    }
  }, [options.onSuccess]);

  // Error callback
  const handleError = useCallback((err) => {
    setToken('');
    setError('Verification failed');
    if (options.onError) {
      options.onError(err);
    }
  }, [options.onError]);

  // Expired callback
  const handleExpired = useCallback(() => {
    setToken('');
    setError('Verification expired');

    if (widgetId.current && (window as any).turnstile) {
      (window as any).turnstile.reset(widgetId.current);
    }

    if (options.onExpired) {
      options.onExpired();
    }
  }, [options.onExpired]);

  // Render widget when ready
  useEffect(() => {
    if (isLoaded && elementId && (window as any).turnstile && !widgetId.current) {
      widgetId.current = (window as any).turnstile.render(elementId, {
        sitekey: process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY,
        callback: handleSuccess,
        'error-callback': handleError,
        'expired-callback': handleExpired,
        theme: options.theme || 'light',
        size: options.size || 'normal',
        action: options.action,
      });
    }
  }, [isLoaded, elementId, widgetId.current, handleSuccess, handleError, handleExpired, options.theme, options.size, options.action]);

  // Reset function
  const reset = useCallback(() => {
    if (widgetId.current && (window as any).turnstile) {
      (window as any).turnstile.reset(widgetId.current);
      setToken('');
      setError(null);
    }
  }, []);

  // Cleanup
  useEffect(() => {
    return () => {
      if (widgetId.current && (window as any).turnstile) {
        (window as any).turnstile.remove(widgetId.current);
      }
    };
  }, []);

  return {
    turnstileToken: token,
    error,
    isLoaded,
    resetTurnstileToken: reset,
  };
};
